/**
 * AG Grid column definitions for Assessments list view.
 * Modernized for Angular 20+ - no moment.js or lodash dependencies.
 */

import { dateValueFormatter as dateFormatter } from '../utils/format-date-cell';

function userValueGetter(field: string) {
  return (params: any) => {
    const val = params.data?.[field];
    if (!val) return '';
    if (typeof val === 'string') return val;
    return val.friendlyName || val.email || '';
  };
}

export const colAssessmentNum = {
  colId: 'assessmentNum',
  headerName: 'Assessment ID',
  field: 'assessmentNum',
  width: 140,
  filter: 'agTextColumnFilter'
};

export const colName = {
  colId: 'name',
  headerName: 'Name',
  field: 'name',
  width: 200,
  filter: 'agTextColumnFilter'
};

export const colType = {
  colId: 'type',
  headerName: 'Type',
  field: 'type',
  width: 180,
  filter: 'agTextColumnFilter'
};

export const colStatus = {
  colId: 'status',
  headerName: 'Status',
  field: 'status',
  width: 150,
  filter: 'agTextColumnFilter'
};

export const colOwner = {
  colId: 'owner',
  headerName: 'Owner',
  field: 'owner',
  width: 150,
  filter: 'agTextColumnFilter',
  valueGetter: userValueGetter('owner')
};

export const colAssessor = {
  colId: 'assessor',
  headerName: 'Assessor',
  field: 'assessor',
  width: 150,
  filter: 'agTextColumnFilter',
  valueGetter: userValueGetter('assessor')
};

export const colFinalReviewer = {
  colId: 'finalReviewer',
  headerName: 'Reviewer',
  field: 'finalReviewer',
  width: 150,
  filter: 'agTextColumnFilter',
  valueGetter: userValueGetter('finalReviewer')
};

export const colAverageScore = {
  colId: 'averageScore',
  headerName: 'Average Score',
  field: 'averageScore',
  width: 120,
  filter: 'agNumberColumnFilter',
  valueFormatter: (params: any) => {
    const val = params.value;
    if (val === undefined || val === null) return '';
    const num = parseFloat(val);
    return isNaN(num) ? '' : num.toFixed(2);
  }
};

export const colRequiredAction = {
  colId: 'requiredAction',
  headerName: 'Required Action',
  field: 'requiredAction',
  width: 180,
  filter: 'agTextColumnFilter'
};

export const colCriticalDate = {
  colId: 'criticalDate',
  headerName: 'Critical Date',
  field: 'criticalDate',
  width: 130,
  valueFormatter: dateFormatter,
  filter: 'agDateColumnFilter'
};

export const colSourceRecord = {
  colId: 'sourceRecord',
  headerName: 'Source Record',
  field: 'sourceRecord',
  width: 180,
  filter: 'agTextColumnFilter',
  valueGetter: (params: any) => {
    return params.data?.sourceRecord || params.data?.contractFriendlyName || params.data?.entityId || '';
  }
};

/** Sum of the question scores (what the legacy SRH app showed). */
export const colTotalScore = {
  colId: 'totalScore',
  headerName: 'Total Score',
  field: 'totalScore',
  width: 110,
  filter: 'agNumberColumnFilter'
};

export const colEntityType = {
  colId: 'entityType',
  headerName: 'Entity Type',
  field: 'entityType',
  width: 120,
  filter: 'agTextColumnFilter'
};

export const colRecommendation = {
  colId: 'recommendation',
  headerName: 'Recommendation',
  field: 'recommendation',
  width: 250,
  filter: 'agTextColumnFilter',
  valueGetter: (params: any) => {
    const html = params.data?.recommendation;
    if (!html) return '';
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const text = temp.textContent || temp.innerText || '';
    return text.length > 150 ? text.substring(0, 150) + '...' : text;
  }
};

export const colCreatedDate = {
  colId: 'createdDate',
  headerName: 'Created',
  field: 'createdDate',
  width: 130,
  valueFormatter: dateFormatter,
  filter: 'agDateColumnFilter'
};

export const allColumns = [
  colAssessmentNum,
  colName,
  colType,
  colStatus,
  colOwner,
  colAssessor,
  colFinalReviewer,
  colAverageScore,
  colTotalScore,
  colRequiredAction,
  colCriticalDate,
  colSourceRecord,
  colEntityType,
  colRecommendation,
  colCreatedDate
];

export const columnSelectList = allColumns.map(col => ({
  value: col,
  viewValue: col.headerName
}));

// Backward-compatible aliases for old column names used by other components
export const colAssessmentName = colName;
export const colAssessmentPartyName = colSourceRecord;
export const colAssessmentType = colType;
export const colAssessors = colAssessor;
export const colContractId = colEntityType;
